/**
 * Digital content subscription catalog helpers (client).
 * Listing publicData.accessMode is 'subscription' | 'unlimited'.
 * Missing accessMode on an existing digital listing is treated as unlimited.
 */

import { LISTING_STATE_PUBLISHED } from './types';
import { createSlug } from './urlHelpers';

export const ACCESS_MODE_SUBSCRIPTION = 'subscription';
export const ACCESS_MODE_UNLIMITED = 'unlimited';

export const SUBSCRIPTION_GRANT_STATUSES = ['trialing', 'active', 'past_due'];
export const SUBSCRIPTION_DENY_STATUSES = ['unpaid', 'canceled', 'incomplete'];

/** UI-only process state for Flex inquire receipts created after Stripe signup. */
export const SUBSCRIPTION_RECEIPT_UI_STATE = 'subscribed';
/** UI-only process state while Stripe will still grant access, but renewal is off. */
export const SUBSCRIPTION_CANCELING_UI_STATE = 'subscriptionCanceling';
/** UI-only process state for canceled subscription receipts (heading copy). */
export const SUBSCRIPTION_CANCELED_UI_STATE = 'subscriptionCanceled';

export const PLAN_INTERVAL_WEEK = 'week';
export const PLAN_INTERVAL_MONTH = 'month';
export const PLAN_INTERVAL_YEAR = 'year';
export const PLAN_INTERVAL_LIFETIME = 'lifetime';
/** Optional one-line subtitle on subscription plans (editor + checkout). */
export const PLAN_DESCRIPTION_MAX = 80;

/** Intervals practitioners can add. Week kept only for legacy plans already on a listing. */
export const SELECTABLE_PLAN_INTERVALS = [
  PLAN_INTERVAL_MONTH,
  PLAN_INTERVAL_YEAR,
  PLAN_INTERVAL_LIFETIME,
];

const PLAN_INTERVALS = new Set([
  PLAN_INTERVAL_WEEK,
  PLAN_INTERVAL_MONTH,
  PLAN_INTERVAL_YEAR,
  PLAN_INTERVAL_LIFETIME,
]);

export const isLifetimeInterval = interval => interval === PLAN_INTERVAL_LIFETIME;

export const usedSelectablePlanIntervals = (plans = []) =>
  new Set(
    (plans || [])
      .map(plan => plan?.interval)
      .filter(interval => SELECTABLE_PLAN_INTERVALS.includes(interval))
  );

export const canAddSubscriptionPlan = (plans = []) =>
  usedSelectablePlanIntervals(plans).size < SELECTABLE_PLAN_INTERVALS.length;

export const nextAvailablePlanInterval = (plans = []) => {
  const used = usedSelectablePlanIntervals(plans);
  return SELECTABLE_PLAN_INTERVALS.find(interval => !used.has(interval)) || null;
};

/** Options for the plan interval select; includes a legacy interval still on this plan row. */
export const selectablePlanIntervals = (currentInterval = null) => {
  const intervals = [...SELECTABLE_PLAN_INTERVALS];
  if (
    currentInterval &&
    PLAN_INTERVALS.has(currentInterval) &&
    !intervals.includes(currentInterval)
  ) {
    intervals.unshift(currentInterval);
  }
  return intervals;
};

export const createPlanId = (interval = PLAN_INTERVAL_MONTH, intervalCount = 1) =>
  `plan_${interval}_${intervalCount}_${Date.now().toString(36)}`;

export const normalizeAccessMode = accessMode =>
  accessMode === ACCESS_MODE_SUBSCRIPTION ? ACCESS_MODE_SUBSCRIPTION : ACCESS_MODE_UNLIMITED;

export const isContentSubscriptionListing = listingOrPublicData => {
  const publicData = listingOrPublicData?.attributes?.publicData || listingOrPublicData || {};
  return normalizeAccessMode(publicData.accessMode) === ACCESS_MODE_SUBSCRIPTION;
};

const protectedDataFrom = transactionOrProtectedData =>
  transactionOrProtectedData?.attributes?.protectedData || transactionOrProtectedData || {};

/** Flex inquire receipt created after a Stripe subscription signup. */
export const isContentSubscriptionReceipt = transactionOrProtectedData => {
  const pd = protectedDataFrom(transactionOrProtectedData);
  return !!(pd.contentSubscriptionSignup || pd.stripeSubscriptionId);
};

const DIGITAL_PRODUCTS_PAID_PROCESS = 'soulwise-digital-products-paid';

/**
 * Inbox/list queries used to omit protectedData, so receipts looked like inquiries.
 * Fall back to a digital-paid inquire on a subscription listing.
 */
export const isContentSubscriptionSale = tx => {
  if (isContentSubscriptionReceipt(tx)) {
    return true;
  }
  const processBase = (tx?.attributes?.processName || '').split('/')[0];
  return (
    processBase === DIGITAL_PRODUCTS_PAID_PROCESS &&
    tx?.attributes?.lastTransition === 'transition/inquire' &&
    isContentSubscriptionListing(tx?.listing)
  );
};

export const contentSubscriptionSignupHasTrial = (protectedData, listing) => {
  const pd = protectedDataFrom(protectedData);
  const signup = pd.contentSubscriptionSignup;
  if (signup && typeof signup === 'object' && signup.hasTrial != null) {
    return !!signup.hasTrial;
  }
  // Older receipts stored signup without hasTrial; listing trial is the fallback.
  if (signup && typeof signup === 'object') {
    return listingHasTrial(listing);
  }
  return false;
};

/** True while a trial window is still open (inbox has no live Stripe status). */
export const isSubscriptionTrialOngoing = (tx, listing, now = new Date()) => {
  if (!contentSubscriptionSignupHasTrial(tx?.attributes?.protectedData || tx, listing)) {
    return false;
  }
  const days = getTrialDays(listing);
  const start = tx?.attributes?.createdAt || tx?.attributes?.lastTransitionedAt;
  const end = getTrialEndDate(days, start);
  if (!end) {
    return true;
  }
  return end.getTime() > (now instanceof Date ? now.getTime() : new Date(now).getTime());
};

const DEFAULT_PROVIDER_COMMISSION_PERCENT = 2.5;

/** Fill a missing provider service fee on quotes that omitted it (older receipts stored 0). */
export const applyProviderFeeToQuote = quote => {
  if (!quote?.currency) {
    return quote;
  }
  const subtotalAmount = quote.subtotalAmount ?? 0;
  if (quote.providerFeeAmount > 0) {
    return {
      ...quote,
      providerPayoutAmount:
        quote.providerPayoutAmount > 0
          ? quote.providerPayoutAmount
          : Math.max(0, subtotalAmount - quote.providerFeeAmount),
    };
  }
  const percent =
    quote.providerFeePercentage != null && quote.providerFeePercentage !== 0
      ? quote.providerFeePercentage
      : DEFAULT_PROVIDER_COMMISSION_PERCENT;
  const providerFeeAmount = Math.round((subtotalAmount * percent) / 100);
  return {
    ...quote,
    providerFeeAmount,
    providerFeePercentage: percent,
    providerPayoutAmount: Math.max(0, subtotalAmount - providerFeeAmount),
  };
};

export const getSubscriptionConfig = listingOrPublicData => {
  const publicData = listingOrPublicData?.attributes?.publicData || listingOrPublicData || {};
  return publicData.subscription || null;
};

export const listingHasTrial = listingOrPublicData => {
  const trial = getSubscriptionConfig(listingOrPublicData)?.trial;
  const days = Number(trial?.days);
  return !!(trial?.enabled && days > 0);
};

export const planAppliesTrial = (listingOrPublicData, plan) =>
  listingHasTrial(listingOrPublicData) && !!plan;

/**
 * Final Form checkboxes with `value="true"` store `['true']` when on and `[]` when off.
 * `!![]` is true, so empty arrays must not count as enabled.
 */
export const isTrialEnabledFormValue = value => {
  if (value === true || value === 'true') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(v => v === true || v === 'true');
  }
  return false;
};

export const confirmStripeBillingSecret = (stripe, clientSecret, { paymentMethodId } = {}) => {
  if (!stripe || !clientSecret) {
    return Promise.resolve({ error: null });
  }
  const confirmData = paymentMethodId ? { payment_method: paymentMethodId } : undefined;
  if (String(clientSecret).startsWith('seti_')) {
    return confirmData
      ? stripe.confirmCardSetup(clientSecret, confirmData)
      : stripe.confirmCardSetup(clientSecret);
  }
  return confirmData
    ? stripe.confirmCardPayment(clientSecret, confirmData)
    : stripe.confirmCardPayment(clientSecret);
};

export const subscriptionCheckoutErrorMessage = err =>
  err?.error || err?.message || err?.statusText || null;

/** Stripe left the subscription incomplete and did not return a secret to confirm. */
export const subscriptionCreateNeedsClientSecret = result =>
  result?.status === 'incomplete' && !result?.clientSecret;

export const getTrialDays = listingOrPublicData => {
  if (!listingHasTrial(listingOrPublicData)) {
    return 0;
  }
  return Number(getSubscriptionConfig(listingOrPublicData).trial.days);
};

export const getTrialEndDate = (trialDays, now = new Date()) => {
  const days = Number(trialDays);
  if (!days || days < 1) {
    return null;
  }
  const end = new Date(now.getTime());
  end.setDate(end.getDate() + days);
  return end;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/** Remaining time until `endDate`. Used for trial end and billing-cycle end copy. */
export const remainingUntil = (endDate, now = new Date()) => {
  if (!endDate) {
    return null;
  }
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(end.getTime())) {
    return null;
  }
  const ms = end.getTime() - (now instanceof Date ? now.getTime() : new Date(now).getTime());
  if (ms <= 0) {
    return { ended: true, unit: 'days', value: 0 };
  }
  if (ms >= DAY_MS) {
    return { ended: false, unit: 'days', value: Math.ceil(ms / DAY_MS) };
  }
  if (ms >= HOUR_MS) {
    return { ended: false, unit: 'hours', value: Math.ceil(ms / HOUR_MS) };
  }
  return { ended: false, unit: 'minutes', value: Math.max(1, Math.ceil(ms / MINUTE_MS)) };
};

export const subscriptionIntervalMessageId = interval => {
  if (interval === PLAN_INTERVAL_WEEK) {
    return 'EditListingPricingForm.intervalWeek';
  }
  if (interval === PLAN_INTERVAL_LIFETIME) {
    return 'EditListingPricingForm.intervalLifetime';
  }
  if (interval === PLAN_INTERVAL_YEAR) {
    return 'EditListingPricingForm.intervalYear';
  }
  return 'EditListingPricingForm.intervalMonth';
};

export const subscriptionPlanLabelMessageId = interval => {
  if (interval === PLAN_INTERVAL_WEEK) {
    return 'CheckoutPage.planWeeklyLabel';
  }
  if (interval === PLAN_INTERVAL_LIFETIME) {
    return 'CheckoutPage.planLifetimeLabel';
  }
  if (interval === PLAN_INTERVAL_YEAR) {
    return 'CheckoutPage.planYearlyLabel';
  }
  return 'CheckoutPage.planMonthlyLabel';
};

export const subscriptionBreakdownMessageId = interval => {
  if (interval === PLAN_INTERVAL_WEEK) {
    return 'CheckoutPage.breakdownWeeklyPlan';
  }
  if (interval === PLAN_INTERVAL_LIFETIME) {
    return 'CheckoutPage.breakdownLifetimePlan';
  }
  if (interval === PLAN_INTERVAL_YEAR) {
    return 'CheckoutPage.breakdownYearlyPlan';
  }
  return 'CheckoutPage.breakdownMonthlyPlan';
};

export const normalizePlan = (plan, fallbackCurrency = 'USD') => {
  if (!plan || plan.interval === 'hour') {
    return null;
  }
  const interval = PLAN_INTERVALS.has(plan.interval) ? plan.interval : PLAN_INTERVAL_MONTH;
  const intervalCount = Math.max(1, parseInt(plan.intervalCount, 10) || 1);
  const amount = Number(plan.amount);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  const description =
    typeof plan.description === 'string'
      ? plan.description.trim().slice(0, PLAN_DESCRIPTION_MAX)
      : '';
  return {
    id: plan.id || createPlanId(interval, intervalCount),
    interval,
    intervalCount,
    amount,
    currency: plan.currency || fallbackCurrency,
    stripePriceId: plan.stripePriceId || null,
    ...(description ? { description } : {}),
  };
};

export const getSubscriptionPlans = (listingOrPublicData, fallbackCurrency = 'USD') => {
  const raw = getSubscriptionConfig(listingOrPublicData)?.plans;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(p => normalizePlan(p, fallbackCurrency)).filter(Boolean);
};

export const getLowestSubscriptionPlan = (listingOrPublicData, fallbackCurrency = 'USD') => {
  const plans = getSubscriptionPlans(listingOrPublicData, fallbackCurrency);
  if (plans.length === 0) {
    return null;
  }
  return [...plans].sort((a, b) => {
    if (a.amount !== b.amount) {
      return a.amount - b.amount;
    }
    if (a.interval === PLAN_INTERVAL_MONTH && b.interval !== PLAN_INTERVAL_MONTH) {
      return -1;
    }
    if (b.interval === PLAN_INTERVAL_MONTH && a.interval !== PLAN_INTERVAL_MONTH) {
      return 1;
    }
    return 0;
  })[0];
};

export const findSubscriptionPlan = (listingOrPublicData, planId, fallbackCurrency = 'USD') => {
  const plans = getSubscriptionPlans(listingOrPublicData, fallbackCurrency);
  if (planId) {
    const match = plans.find(p => p.id === planId);
    if (match) {
      return match;
    }
  }
  return getLowestSubscriptionPlan(listingOrPublicData, fallbackCurrency);
};

export const subscriptionPaymentQuoteFromProtectedData = (protectedData, listing) => {
  const pd = protectedDataFrom(protectedData);
  const signup = pd.contentSubscriptionSignup;
  const quote = signup && typeof signup === 'object' ? signup.paymentQuote : null;
  if (quote?.currency && (quote.totalAmount != null || quote.subtotalAmount != null)) {
    const subtotalAmount = quote.subtotalAmount ?? 0;
    const feeAmount = quote.feeAmount || 0;
    return applyProviderFeeToQuote({
      currency: quote.currency,
      subtotalAmount,
      feeAmount,
      totalAmount: quote.totalAmount ?? subtotalAmount + feeAmount,
      providerFeeAmount: quote.providerFeeAmount,
      providerFeePercentage: quote.providerFeePercentage,
      providerPayoutAmount: quote.providerPayoutAmount,
    });
  }
  const plan = findSubscriptionPlan(listing, pd.planId || signup?.planId);
  if (!plan) {
    return null;
  }
  return applyProviderFeeToQuote({
    currency: plan.currency,
    subtotalAmount: plan.amount,
    feeAmount: 0,
    totalAmount: plan.amount,
  });
};

export const validateSubscriptionPlans = (plans, { minAmount = 0 } = {}) => {
  if (!Array.isArray(plans) || plans.length === 0) {
    return { valid: false, error: 'planRequired' };
  }
  for (const plan of plans) {
    const normalized = normalizePlan(plan);
    if (!normalized) {
      return { valid: false, error: 'planInvalid' };
    }
    if (normalized.amount < minAmount) {
      return { valid: false, error: 'planPriceTooLow' };
    }
  }
  return { valid: true };
};

export const validateTrial = trial => {
  if (!trial?.enabled) {
    return { valid: true };
  }
  const days = Number(trial.days);
  if (!Number.isFinite(days) || days < 1 || days > 365) {
    return { valid: false, error: 'trialDaysRequired' };
  }
  return { valid: true };
};

export const subscriptionGrantsAccess = status => SUBSCRIPTION_GRANT_STATUSES.includes(status);

/** True while Stripe will still grant access, but renewal/trial conversion is off. */
export const isSubscriptionCancelScheduled = subscription => {
  const { status, cancelAtPeriodEnd } = subscription || {};
  return !!cancelAtPeriodEnd && subscriptionGrantsAccess(status);
};

/** Inbox/heading signal: operator wrote this when cancel-at-period-end was scheduled. */
export const isTransactionSubscriptionCancelScheduled = transaction => {
  const pd = protectedDataFrom(transaction);
  return !!pd.subscriptionCancelScheduled;
};

/** Active subscription receipt heading/inbox state (subscribed vs canceling). */
export const subscriptionReceiptProcessState = transaction =>
  isTransactionSubscriptionCancelScheduled(transaction)
    ? SUBSCRIPTION_CANCELING_UI_STATE
    : SUBSCRIPTION_RECEIPT_UI_STATE;

/**
 * Prefer live Stripe on the booking page so returning from the portal updates
 * the heading before (or after) protectedData catches up.
 */
export const subscriptionHeadingProcessState = (processState, liveSubscription) => {
  const isReceiptHeading =
    processState === SUBSCRIPTION_RECEIPT_UI_STATE ||
    processState === SUBSCRIPTION_CANCELING_UI_STATE;
  if (!isReceiptHeading || !liveSubscription?.status) {
    return processState;
  }
  return isSubscriptionCancelScheduled(liveSubscription)
    ? SUBSCRIPTION_CANCELING_UI_STATE
    : SUBSCRIPTION_RECEIPT_UI_STATE;
};

export const SUBSCRIPTION_END_KIND_CANCELED = 'canceled';
export const SUBSCRIPTION_END_KIND_UNPAID = 'unpaid';
export const SUBSCRIPTION_END_KIND_INACTIVE = 'inactive';

export const isListingAvailableForPurchase = listing => {
  const attrs = listing?.attributes || {};
  const trash = attrs.privateData?.trash;
  const isTrashed = !!trash && typeof trash === 'object';
  return !attrs.deleted && !isTrashed && attrs.state === LISTING_STATE_PUBLISHED;
};

export const isLibraryListingAvailableForPurchase = group => {
  if (!group?.listingId || group.listingDeleted) {
    return false;
  }
  if (group.listingState && group.listingState !== LISTING_STATE_PUBLISHED) {
    return false;
  }
  return true;
};

export const getSubscriptionEndKind = subscription => {
  const { status, canceledBy } = subscription || {};
  if (!status || subscriptionGrantsAccess(status) || status === 'incomplete') {
    return null;
  }
  if (canceledBy === 'unpaid' || status === 'unpaid') {
    return SUBSCRIPTION_END_KIND_UNPAID;
  }
  if (status === 'canceled' && canceledBy !== 'listing_closed') {
    return SUBSCRIPTION_END_KIND_CANCELED;
  }
  return SUBSCRIPTION_END_KIND_INACTIVE;
};

export const subscriptionEndListMessageId = kind => {
  if (kind === SUBSCRIPTION_END_KIND_CANCELED) {
    return 'LibraryListingCard.subscriptionCanceled';
  }
  if (kind === SUBSCRIPTION_END_KIND_UNPAID) {
    return 'LibraryListingCard.subscriptionUnpaid';
  }
  if (kind === SUBSCRIPTION_END_KIND_INACTIVE) {
    return 'LibraryListingCard.subscriptionInactive';
  }
  return null;
};

export const subscriptionEndDetailMessageId = kind => {
  if (kind === SUBSCRIPTION_END_KIND_CANCELED) {
    return 'ContentLibraryDetailPage.subscriptionCanceled';
  }
  if (kind === SUBSCRIPTION_END_KIND_UNPAID || kind === SUBSCRIPTION_END_KIND_INACTIVE) {
    return 'ContentLibraryDetailPage.subscriptionInactive';
  }
  return null;
};

export const checkoutNamedLinkProps = listingOrGroup => {
  const id = listingOrGroup?.id?.uuid || listingOrGroup?.listingId;
  const title = listingOrGroup?.attributes?.title || listingOrGroup?.listingTitle || '';
  if (!id) {
    return null;
  }
  return {
    name: 'CheckoutPage',
    params: { id, slug: createSlug(title) },
  };
};

export const getSubscriptionCtaVariant = ({ status, hasTrial, trialEligible = true } = {}) => {
  if (status === 'active') {
    return 'view_content';
  }
  if (status === 'expired' || status === 'none' || !status) {
    if (hasTrial && trialEligible) {
      return 'try_for_free';
    }
    return 'subscribe';
  }
  return 'subscribe';
};

export const getSubscriptionTrackingFields = (listing, { plan, ctaVariant } = {}) => {
  const publicData = listing?.attributes?.publicData || {};
  const accessMode = normalizeAccessMode(publicData.accessMode);
  const hasTrial = listingHasTrial(publicData);
  const selected = plan || getLowestSubscriptionPlan(publicData);
  return {
    access_mode: accessMode,
    has_trial: hasTrial,
    trial_days: getTrialDays(publicData) || undefined,
    plan_id: selected?.id,
    plan_interval: selected?.interval,
    plan_price: selected ? selected.amount / 100 : undefined,
    cta_variant: ctaVariant,
  };
};

export const sumCustomerLineItemAmounts = lineItems => {
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    return null;
  }
  const customerItems = lineItems.filter(
    item => item?.includeFor?.includes('customer') && !item.reversal && item.lineTotal
  );
  if (customerItems.length === 0) {
    return null;
  }
  return {
    amount: customerItems.reduce((sum, item) => sum + (item.lineTotal.amount || 0), 0),
    currency: customerItems[0].lineTotal.currency,
  };
};

export const defaultSubscriptionFormPlans = (currency, existingPriceAmount) => [
  {
    id: createPlanId(PLAN_INTERVAL_MONTH, 1),
    interval: PLAN_INTERVAL_MONTH,
    intervalCount: 1,
    amount: existingPriceAmount || 0,
    currency,
    stripePriceId: null,
  },
];
